import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import GridViewIcon from "@mui/icons-material/GridView";
import { swayyColors, swayyFont } from "../../theme/tokens";

export interface SwayyMenu {
  title: string;
  path: string;
  icon?: JSX.Element;
}

interface AppSideDrawerProps {
  open: boolean;
  handleDrawerClose: () => void;
  menus: SwayyMenu[];
}

const drawerWidth = 240;

const AppSideDrawer: React.FC<AppSideDrawerProps> = ({
  open,
  handleDrawerClose,
  menus,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isMenuActive = (menuPath: string) => {
    if (menuPath === "" || menuPath === "/") {
      return location.pathname === "/" || location.pathname === "";
    }
    return location.pathname.startsWith(menuPath);
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List disablePadding sx={{ px: 1.25, pt: 2.5 }}>
        {menus.map((menu) => {
          const active = isMenuActive(menu.path);
          return (
            <Tooltip
              key={menu.title}
              title={menu.title}
              placement="right"
              disableHoverListener
            >
              <Link
                to={menu.path}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={isMobile ? handleDrawerClose : undefined}
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    sx={{
                      borderRadius: 0,
                      px: 1.5,
                      py: 1,
                      minHeight: 42,
                      bgcolor: active
                        ? "rgba(11, 182, 174, 0.16)"
                        : "transparent",
                      borderLeft: active
                        ? `2px solid ${swayyColors.pine}`
                        : "2px solid transparent",
                      "&:hover": {
                        bgcolor: active
                          ? "rgba(11, 182, 174, 0.22)"
                          : "rgba(148, 163, 184, 0.1)",
                      },
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <Box
                      sx={{
                        mr: 1.5,
                        fontSize: 14,
                        color: active
                          ? swayyColors.pine
                          : swayyColors.textOnChromeMuted,
                        display: "flex",
                        alignItems: "center",
                        minWidth: 18,
                        justifyContent: "center",
                      }}
                    >
                      {menu.icon ? (
                        menu.icon
                      ) : (
                        <GridViewIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                    <ListItemText
                      primary={menu.title}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: active ? 600 : 500,
                        letterSpacing: "0.01em",
                        color: active
                          ? swayyColors.white
                          : swayyColors.textOnChrome,
                        fontFamily: swayyFont,
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Link>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={open}
      onClose={handleDrawerClose}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          bgcolor: swayyColors.sidebarBg,
          borderRight: "none",
          borderRadius: 0,
          top: "56px",
          height: "calc(100% - 56px)",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AppSideDrawer;
